import React from "react";
import { Link } from "react-router-dom";
import Seo from "../../../components/public/Seo";
import "../../../styles/legal.css";

export default function MerchantTerms() {
  return (
    <>
      <Seo
        title="Merchant Terms — Veya"
        description="Terms for boutiques and merchants using Veya."
      />

      <main className="legal-page">
        <section className="legal-hero legal-hero--narrow">
          <p className="legal-kicker">Merchant Terms</p>
          <h1>Merchant Terms</h1>
          <p>
            These terms apply to boutiques, retailers and merchants using Veya
            to manage products, appear in shopper experiences, and access
            merchant-facing tools or insights.
          </p>
          <p className="legal-updated">Last updated: May 2026</p>
        </section>

        <article className="legal-document">
          <section>
            <h2>1. Relationship with Veya</h2>
            <p>
              Veya provides a digital fashion experience and intelligence layer
              for boutiques and shoppers. Unless expressly agreed otherwise,
              Veya is not the seller of merchant products.
            </p>
            <p>
              Merchants remain responsible for their own business, products,
              customer relationships, legal obligations, pricing, fulfilment,
              returns and customer service.
            </p>
          </section>

          <section>
            <h2>2. Merchant accounts</h2>
            <p>
              You must provide accurate business, contact and account
              information when creating or maintaining a merchant account.
            </p>
            <p>
              You are responsible for keeping your account secure and for all
              activity under your merchant login.
            </p>
          </section>

          <section>
            <h2>3. Catalogue and product information</h2>
            <p>
              Merchants are responsible for ensuring all catalogue and product
              information supplied to Veya is accurate, lawful, current and not
              misleading.
            </p>
            <p>This includes, where relevant:</p>
            <ul>
              <li>product titles and descriptions;</li>
              <li>prices, sale prices and currency;</li>
              <li>availability and stock status;</li>
              <li>images and product media;</li>
              <li>sizes, fit notes, materials and colours;</li>
              <li>brand and supplier information;</li>
              <li>shipping, returns and refund policies;</li>
              <li>product URLs and checkout links.</li>
            </ul>
          </section>

          <section>
            <h2>4. Image and content rights</h2>
            <p>
              You must have the right to provide any product images, brand
              assets, descriptions, logos, catalogue files or other content you
              upload or connect to Veya.
            </p>
            <p>
              You give Veya permission to host, copy, display, resize, analyse,
              process and use merchant content as reasonably needed to provide
              Veya’s shopper, try-on, catalogue, discovery, edit and insight
              features.
            </p>
          </section>

          <section>
            <h2>5. Try-on and visualisation</h2>
            <p>
              Veya may process product imagery or catalogue data to support
              visual try-on or styling features.
            </p>
            <p>
              Try-on is for visualisation only. Real fit, sizing, colour,
              garment behaviour, material appearance and customer experience may
              vary.
            </p>
            <p>
              Merchants should not represent Veya try-on as a guarantee of fit,
              size, comfort, quality, performance or suitability.
            </p>
          </section>

          <section>
            <h2>6. Merchant insights</h2>
            <p>
              Veya may provide merchants with insights based on shopper activity,
              product engagement, saved items, add-to-bag activity, edits,
              try-ons, style signals and catalogue patterns.
            </p>
            <p>
              Insights are designed to support decision-making. They are not a
              promise or guarantee of demand, sales, conversion, stock
              performance or commercial outcomes.
            </p>
            <p>
              You are responsible for how you interpret and act on insights
              provided by Veya.
            </p>
          </section>

          <section>
            <h2>7. Consumer and trading obligations</h2>
            <p>
              Merchants are responsible for complying with applicable consumer,
              advertising, privacy, product safety, tax, e-commerce and trading
              laws.
            </p>
            <p>
              This includes ensuring product claims, pricing, discounts,
              promotions, availability and policies are accurate and not
              misleading.
            </p>
          </section>

          <section>
            <h2>8. Orders, payment, fulfilment and returns</h2>
            <p>
              Unless Veya expressly agrees otherwise, the merchant is
              responsible for orders, payment processing, stock management,
              fulfilment, delivery, returns, exchanges, refunds, chargebacks,
              customer enquiries and after-sales service.
            </p>
          </section>

          <section>
            <h2>9. Prohibited merchant use</h2>
            <p>You must not use Veya to:</p>
            <ul>
              <li>upload false, misleading or unlawful product information;</li>
              <li>sell counterfeit, unsafe, unlawful or unauthorised goods;</li>
              <li>infringe intellectual property or image rights;</li>
              <li>misrepresent your business, stock, pricing or policies;</li>
              <li>interfere with Veya’s platform, data or security;</li>
              <li>attempt to access shopper information without permission;</li>
              <li>use insights to discriminate, target unfairly or act unlawfully.</li>
            </ul>
          </section>

          <section>
            <h2>10. Suspension or removal</h2>
            <p>
              Veya may suspend, restrict or remove merchant access, products,
              catalogue content or public visibility if we believe these terms
              have been breached, content is inaccurate or unlawful, there is a
              security risk, or continued availability may harm Veya, shoppers,
              merchants or third parties.
            </p>
          </section>

          <section>
            <h2>11. Privacy</h2>
            <p>
              Merchants must handle any shopper, customer or platform
              information they receive through Veya in accordance with
              applicable privacy laws and Veya’s{" "}
              <Link to="/legal/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <section>
            <h2>12. Changes to merchant features</h2>
            <p>
              Veya may add, change, limit or remove merchant features,
              dashboards, import tools, insights, integrations or public display
              options over time.
            </p>
          </section>

          <section>
            <h2>13. Contact</h2>
            <p>
              For merchant legal or platform questions, contact{" "}
              <a href="mailto:hello@veya.co.nz">hello@veya.co.nz</a>.
            </p>
          </section>
        </article>
      </main>
    </>
  );
}